import { useMemo, useState } from 'react';
import { useT, type MessageKey } from '../lib/i18n';
import { TYPE_KEY, labelOf, type FlowDefinition, type RegistryDefinition, type StateDefinition } from '../lib/registry';
import { actionsOf, flowOf, slug, unique, type ActionDefinition } from '../lib/flowDesign';
import { directiveId, listOf, rootOf, templateId, valueId, valuesOf, type DirectiveList } from '../lib/taxonomyGraph';
import { KindIcon } from './icons';

export type NewKind = 'object' | 'event' | 'document' | 'value' | 'flow' | 'action' | 'automation' | 'rules' | 'bindings'
  | 'mappings' | 'mapLayers' | 'boards' | 'views' | 'screens' | 'menu' | 'dashboards' | 'queries' | 'packages' | 'link';

const SECTIONS: { key: string; kinds: NewKind[] }[] = [
  { key: 'data', kinds: ['object', 'event', 'document', 'value', 'link'] },
  { key: 'process', kinds: ['flow', 'action', 'automation', 'rules', 'bindings', 'mappings'] },
  { key: 'view', kinds: ['mapLayers', 'boards', 'views', 'screens', 'menu', 'dashboards', 'queries'] },
  { key: 'access', kinds: ['packages'] }
];

const ICON: Record<NewKind, string> = {
  object: 'object', event: 'event', document: 'document', value: 'value', link: 'links', flow: 'flows', action: 'actions',
  automation: 'automation', rules: 'rules', bindings: 'bindings', mappings: 'mappings', mapLayers: 'mapLayers', boards: 'boards', views: 'views',
  screens: 'screens', menu: 'menu', dashboards: 'dashboards', queries: 'queries', packages: 'packages'
};

type Starter = 'empty' | 'pipeline' | 'approval' | 'production' | 'copy';

/** The states a new flow starts with, in the person's language. */
function starterStates(starter: Starter, t: (k: MessageKey) => string, locale: string): StateDefinition[] {
  const s = (key: string, colour: string, roles?: StateDefinition['roles']): StateDefinition =>
    ({ key, labels: { [locale]: t(`tx.starter.state.${key}` as MessageKey) }, colour, ...(roles ? { roles } : {}) });
  switch (starter) {
    case 'pipeline': return [s('new', '#6b7280', ['start']), s('in-progress', '#2b5b86'), s('waiting', '#8a6100'), s('done', '#2f6b46', ['done']), s('cancelled', '#a4373a', ['lost'])];
    case 'approval': return [s('open', '#2b5b86', ['start']), s('approved', '#2f6b46', ['won']), s('rejected', '#a4373a', ['lost'])];
    case 'production': return [s('planned', '#6b7280', ['start']), s('in-production', '#2b5b86'), s('ready', '#8a6100'), s('delivered', '#2f6b46', ['done'])];
    default: return [s('new', '#6b7280', ['start']), s('done', '#2f6b46', ['done'])];
  }
}

/** A blank directive of a kind, as little as the registry takes for one. */
function blank(list: DirectiveList, key: string, about?: string): Record<string, unknown> {
  const on = about ?? 'data.object';
  switch (list) {
    // A GeoNexora map layer (MapLayerDef): what it draws; the rest - menu, style, columns - is written in it afterwards.
    case 'mapLayers': return { key, type: on, kind: 'thematic', visible: false, style: { mode: 'simple' } };
    case 'boards': return { key, shows: on, roles: { title: '@title', state: '@state', since: '@stateSince' } };
    case 'views': return { key, type: on, kind: 'tile', sections: [{ key: 'main', items: ['@title'] }] };
    case 'screens': return { key, path: `/${key.split('.').at(-1)}`, root: { id: 'root', component: 'stack', children: [] } };
    case 'bindings': return { key, handler: 'rules.evaluate', major: 1, appliesTo: on, when: { on: 'saved' }, inputs: {}, outputs: {} };
    case 'rules': return { key, appliesTo: on, rules: [] };
    case 'mappings': return { key, target: on, source: 'file', columns: [] };
    case 'menu': return { key, group: 'work', order: 50, opens: 'screen:/', needs: [] };
    case 'packages': return { key, kind: 'package', requires: [], members: [] };
    case 'dashboards': return { key, widgets: [] };
    case 'queries': return { key, from: on, measures: [{ key: 'count', fn: 'count' }] };
    default: return { key };
  }
}

const PREFIX: Partial<Record<NewKind, string>> = {
  flow: 'flow.', mapLayers: 'map-layer.', rules: 'rules.', bindings: 'binding.', mappings: 'mapping.', boards: 'board.', views: 'view.',
  screens: 'screen.', menu: 'menu.', dashboards: 'dashboard.', queries: 'query.'
};

/**
 * Making a new part of the taxonomy (docs/en/43 sec.8): first what it is - a card per kind,
 * each saying in a line what it is for - then a name, from which its key is made, and only what
 * that kind cannot be made without. A flow starts from a pattern; a button or an automation
 * from what it is to do. The rest is filled in where it is then opened.
 */
export function NewPartDialog({ definition, onCancel, onMake, initialKind, about }: {
  definition: RegistryDefinition;
  onCancel: () => void;
  /** The definition with the new part, and what to open. */
  onMake: (next: RegistryDefinition, open: string, focus: boolean) => void;
  initialKind?: NewKind;
  /** The template that was chosen when the dialog was opened: what a directive is about by default. */
  about?: string;
}) {
  const { t, locale } = useT();
  const [kind, setKind] = useState<NewKind | null>(initialKind ?? null);
  const [name, setName] = useState('');
  const [keyTyped, setKeyTyped] = useState<string | null>(null);
  const [parent, setParent] = useState('');
  const [target, setTarget] = useState(about ?? '');
  const [starter, setStarter] = useState<Starter>('pipeline');
  const [copyOf, setCopyOf] = useState('');
  const [useFor, setUseFor] = useState(about ?? '');
  const [does, setDoes] = useState<'move' | 'create' | 'message' | 'note'>('move');
  const [state, setState] = useState('');
  const [linkTo, setLinkTo] = useState('');

  const templates = useMemo(() => definition.types.slice().sort((a, b) => labelOf(a.labels, locale, a.key).localeCompare(labelOf(b.labels, locale, b.key))), [definition, locale]);
  const taken = useMemo(() => new Set<string>([
    ...definition.types.map((x) => x.key), ...valuesOf(definition).map((v) => v.key), ...(definition.links ?? []).map((l) => l.key),
    ...Object.values(definition).flatMap((v) => (Array.isArray(v) ? v.map((x) => (x as { key?: string }).key ?? '') : []))
  ]), [definition]);

  const short = (target || 'record').split('.').at(-1);
  const suggested = kind == null ? '' : (() => {
    const s = slug(name) || 'new';
    if (kind === 'action' || kind === 'automation') return unique(`action.${short}.${s}`, taken);
    if (kind === 'link') return unique(s.replace(/-([a-z])/g, (_, c: string) => c.toUpperCase()), taken);
    if (kind === 'object' || kind === 'event' || kind === 'document') return unique(parent ? `${parent}.${s}` : s, taken);
    return unique(`${PREFIX[kind] ?? ''}${s}`, taken);
  })();
  const key = keyTyped ?? suggested;
  const keyOk = (kind === 'link' ? /^[a-z][A-Za-z0-9]*$/ : TYPE_KEY).test(key) && !taken.has(key);
  const flowOfTarget = flowOf(definition, target);
  const needsTarget = kind === 'action' || kind === 'automation' || kind === 'mapLayers';
  const ready = kind != null && name.trim() !== '' && keyOk
    && (!needsTarget || target !== '')
    && (kind !== 'automation' || state !== '')
    && (kind !== 'link' || (target !== '' && linkTo !== ''))
    && (kind !== 'flow' || starter !== 'copy' || copyOf !== '');

  function make() {
    if (!kind || !ready) return;
    const labels = { [locale]: name.trim() };
    const d = definition as RegistryDefinition & Record<string, unknown>;
    if (kind === 'object' || kind === 'event' || kind === 'document') {
      const type = parent ? { key, parent, labels, fields: [] } : { key, root: kind, binding: 'dynamic', labels, fields: [] };
      onMake({ ...definition, types: [...definition.types, type] }, templateId(key), false);
    } else if (kind === 'value') {
      onMake({ ...d, values: [...valuesOf(definition), { key, labels, fields: [{ key: 'name', type: 'text' }] }] } as RegistryDefinition, valueId(key), false);
    } else if (kind === 'link') {
      onMake({ ...definition, links: [...(definition.links ?? []), { key, from: target, to: linkTo, cardinality: 'many-to-many', labels }] }, `link:${key}`, false);
    } else if (kind === 'flow') {
      const source = (definition.flows ?? []).find((f) => f.key === copyOf);
      const flow: FlowDefinition = starter === 'copy' && source
        ? { ...structuredClone(source), key, labels }
        : { key, labels, states: starterStates(starter, t as (k: MessageKey) => string, locale) };
      const types = useFor ? definition.types.map((x) => {
        if (x.key !== useFor) return x;
        const next = { ...x, flow: key };
        delete next.lifecycle;
        return next;
      }) : definition.types;
      onMake({ ...definition, types, flows: [...(definition.flows ?? []), flow] }, directiveId('flows', key), true);
    } else if (kind === 'action' || kind === 'automation') {
      const action: ActionDefinition = { key, target, labels };
      if (kind === 'automation') {
        action.when = { on: 'entered', state };
        action.effects = [];
      } else {
        action.permission = does === 'move' ? 'move' : 'update';
        action.effects = does === 'move' && state ? [{ kind: 'move', state }]
          : does === 'create' ? [{ kind: 'create', as: 'made' }]
            : does === 'message' ? [{ kind: 'message' }]
              : [{ kind: 'note', labels: { [locale]: name.trim() } }];
        if (does === 'move' && flowOfTarget?.states.find((s) => s.key === state)?.roles?.includes('lost')) action.style = 'danger';
      }
      onMake({ ...d, actions: [...actionsOf(definition), action] } as RegistryDefinition, directiveId('actions', key), false);
    } else {
      const list = kind as DirectiveList;
      onMake({ ...d, [list]: [...listOf(definition, list), { ...blank(list, key, target || undefined), labels }] } as RegistryDefinition,
        directiveId(list, key), false);
    }
  }

  const templateSelect = (value: string, onChange: (v: string) => void, testid: string, filter?: (k: string) => boolean) => (
    <select value={value} data-testid={testid} onChange={(e) => onChange(e.target.value)}>
      <option value="">{t('taxonomy.choose')}</option>
      {templates.filter((x) => !filter || filter(x.key)).map((x) => (
        <option key={x.key} value={x.key}>{labelOf(x.labels, locale, x.key)} · {x.key}</option>
      ))}
    </select>
  );

  return (
    <div className="modal-backdrop" role="dialog" aria-modal="true" aria-labelledby="tx-new-title" data-testid="tx-new-dialog"
         onKeyDown={(e) => { if (e.key === 'Escape') onCancel(); }}>
      <div className="modal panel tx-new">
        <header className="tx-new-head">
          <h3 id="tx-new-title">{kind ? t('tx.new.titleKind', { kind: t(`tx.new.kind.${kind}` as MessageKey) }) : t('tx.new.title')}</h3>
          {kind && !initialKind && <button className="link" onClick={() => setKind(null)}>{t('tx.new.back')}</button>}
          <span className="spacer" />
          <button className="ghost" onClick={onCancel} aria-label={t('common.cancel')}>✕</button>
        </header>

        {!kind && SECTIONS.map((section) => (
          <section key={section.key} className="tx-new-section">
            <h4>{t(`tx.new.section.${section.key}` as MessageKey)}</h4>
            <div className="tx-new-grid">
              {section.kinds.map((k) => (
                <button key={k} className={`tx-new-card tx-new-${section.key}`} data-testid={`tx-new-kind-${k}`} onClick={() => setKind(k)}>
                  <span className="tx-new-icon"><KindIcon kind={ICON[k]} size={18} /></span>
                  <strong>{t(`tx.new.kind.${k}` as MessageKey)}</strong>
                  <span className="hint">{t(`tx.new.what.${k}` as MessageKey)}</span>
                </button>
              ))}
            </div>
          </section>
        ))}

        {kind && (
          <div className="tx-new-form">
            <p className="hint">{t(`tx.new.what.${kind}` as MessageKey)}</p>
            <label className="field"><span>{t('tx.new.name')}</span>
              <input value={name} autoFocus data-testid="tx-new-name" placeholder={t(`tx.new.example.${kind}` as MessageKey)}
                     onChange={(e) => setName(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') make(); }} /></label>

            {(kind === 'object' || kind === 'event' || kind === 'document') && (
              <label className="field"><span>{t('tx.new.extends')}</span>
                {templateSelect(parent, setParent, 'tx-new-parent', (k) => rootOf(definition, k) === kind)}
                <span className="hint">{t('tx.new.extendsHint')}</span></label>
            )}

            {(needsTarget || ['mapLayers', 'boards', 'views', 'rules', 'bindings', 'mappings', 'queries', 'link'].includes(kind)) && (
              <label className="field"><span>{t(kind === 'link' ? 'tx.new.linkFrom' : 'tx.new.about')}</span>
                {templateSelect(target, (v) => { setTarget(v); setState(''); }, 'tx-new-target')}</label>
            )}
            {kind === 'link' && (
              <label className="field"><span>{t('tx.new.linkTo')}</span>{templateSelect(linkTo, setLinkTo, 'tx-new-link-to')}</label>
            )}

            {kind === 'flow' && (
              <>
                <div className="field"><span>{t('tx.new.startFrom')}</span>
                  <div className="tx-starters">
                    {(['pipeline', 'approval', 'production', 'empty', 'copy'] as Starter[]).map((s) => (
                      <button key={s} className={`tx-starter${starter === s ? ' on' : ''}`} data-testid={`tx-starter-${s}`} onClick={() => setStarter(s)}>
                        <strong>{t(`tx.starter.${s}` as MessageKey)}</strong>
                        {s !== 'copy' && (
                          <span className="tx-starter-states">
                            {starterStates(s, t as (k: MessageKey) => string, locale).map((st) => (
                              <span key={st.key} style={{ borderColor: st.colour ?? undefined }}>{labelOf(st.labels, locale, st.key)}</span>
                            ))}
                          </span>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
                {starter === 'copy' && (
                  <label className="field"><span>{t('tx.new.copyOf')}</span>
                    <select value={copyOf} data-testid="tx-new-copy" onChange={(e) => setCopyOf(e.target.value)}>
                      <option value="">{t('taxonomy.choose')}</option>
                      {(definition.flows ?? []).map((f) => <option key={f.key} value={f.key}>{labelOf(f.labels, locale, f.key)}</option>)}
                    </select></label>
                )}
                <label className="field"><span>{t('tx.new.useFor')}</span>
                  {templateSelect(useFor, setUseFor, 'tx-new-use-for')}
                  <span className="hint">{t('tx.new.useForHint')}</span></label>
              </>
            )}

            {kind === 'action' && (
              <div className="field"><span>{t('tx.new.does')}</span>
                <div className="tx-segment">
                  {(['move', 'create', 'message', 'note'] as const).map((k) => (
                    <button key={k} className={does === k ? 'on' : ''} data-testid={`tx-new-does-${k}`} onClick={() => setDoes(k)}>{t(`tx.new.does.${k}` as MessageKey)}</button>
                  ))}
                </div>
              </div>
            )}
            {((kind === 'action' && does === 'move') || kind === 'automation') && target && (
              <label className="field"><span>{t(kind === 'automation' ? 'tx.action.whenEnters' : 'tx.new.moveTo')}</span>
                {flowOfTarget ? (
                  <select value={state} data-testid="tx-new-state" onChange={(e) => setState(e.target.value)}>
                    <option value="">{t('taxonomy.choose')}</option>
                    {flowOfTarget.states.map((s) => <option key={s.key} value={s.key}>{labelOf(s.labels, locale, s.key)}</option>)}
                  </select>
                ) : <span className="hint problem">{t('tx.action.noFlow')}</span>}
              </label>
            )}

            <details className="tx-new-key">
              <summary>{t('tx.new.key')} <code>{key}</code></summary>
              <input value={key} spellCheck={false} className="mono" data-testid="tx-new-key" onChange={(e) => setKeyTyped(e.target.value.trim())} />
              <span className="hint">{t('tx.new.keyHint')}</span>
            </details>
            {name && !keyOk && <p className="hint problem">{t('taxonomy.keyBad')}</p>}

            <div className="tab-actions">
              <span className="spacer" />
              <button onClick={onCancel}>{t('common.cancel')}</button>
              <button className="primary" disabled={!ready} onClick={make} data-testid="tx-new-make">{t('tx.new.make')}</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
